import { type GetBackAttivitaV2IdComunicazioniParams } from '@framework/api-client/api/backoffice/schemas/getBackAttivitaV2IdComunicazioniParams.ts';
import { type ComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/comunicazioneDTO.ts';
import { handlePaginationChange } from '@framework/ui/core/pagination/handlePaginationChange.ts';
import { getPaginationUrlColumns } from '@framework/ui/core/pagination/paginationUrlColumns.ts';
import { type PaginationProps } from 'antd';
import { useMemo } from 'react';
import { type ICommunicationListQuery } from '../../CommunicationList.tsx';
import { useCommunicationsContext } from '../../context/CommunicationsContext.tsx';
import { getCommunicationActionColumn } from '../components/getCommunicationActionColumn.tsx';
import { useCommunicationColumns } from './useCommunicationColumns.tsx';

interface IProps {
  query: ICommunicationListQuery;
  defaultQuery: ICommunicationListQuery;
  setQuery: (query: ICommunicationListQuery) => void;
  total: number;

  isBackoffice?: boolean;

  onDelete?: (id: string) => Promise<void>;
  onEdit?: (record: any) => void;
}

export function useCommunicationPaginationUrl(input: IProps) {
  const { query, total, defaultQuery, setQuery, onDelete, onEdit } = input;

  const {
    procedureId,
    parentProcedureId,
    isProcedureOpen,
    isEditable,
    taskId,
    submissionId,
    isBackoffice,
  } = useCommunicationsContext();

  const communicationColumns = useCommunicationColumns();

  const columns = useMemo(() => {
    const urlColumns = getPaginationUrlColumns<
      ComunicazioneDTO,
      GetBackAttivitaV2IdComunicazioniParams
    >(communicationColumns, query, {});

    // Le azioni sono consentite solo per le comunicazioni
    // relative alle attività o alle richieste.
    // Se non ci sono gli id allora ci troviamo in un procedimento
    // e le nascondiamo.
    if (!taskId && !submissionId) {
      return urlColumns;
    }

    return [
      ...urlColumns,
      getCommunicationActionColumn({
        onDelete,
        onEdit,
        procedureId,
        parentProcedureId,
        isProcedureOpen,
        isEditable,
        isBackoffice,
      }),
    ];
  }, [
    communicationColumns,
    isBackoffice,
    isEditable,
    isProcedureOpen,
    onDelete,
    onEdit,
    parentProcedureId,
    procedureId,
    query,
    submissionId,
    taskId,
  ]);

  const paginationOptions = useMemo(() => {
    const onPaginationChange = handlePaginationChange<
      ComunicazioneDTO,
      ICommunicationListQuery
    >({
      columns,
      defaultQuery,
      query,
      setQuery,
      getFilters: () => ({}),
    });

    return {
      current: Number(query.pagina ?? 0) + 1,
      pageSize: query.limite != null ? Number(query.limite) : 10,
      total: total ?? 0,
      pageSizeOptions: [5, 10, 20, 50],
      // La paginazione è "esterna" alla tabella, quindi è necessario
      // passare i campi "a mano", incluso l'ordinamento. Altrimenti,
      // al cambio di pagina l'ordinamento verrebbe perso.
      onChange: (page, pageSize) =>
        onPaginationChange(
          { current: page, pageSize },
          {},
          {
            order: query.ordinamenti?.[0].startsWith('-')
              ? 'descend'
              : 'ascend',
            field: query.ordinamenti?.[0].replace(/^-/, ''),
          },
        ),
    } satisfies PaginationProps;
  }, [columns, defaultQuery, query, setQuery, total]);

  return { columns, paginationOptions };
}
